import { useState } from "react";
import { MonitoringProvider, MONITORING_PROVIDERS } from "../lib/api";
import { useNavigate } from "react-router-dom";
import { Copy, Mail, MessageCircle, Link as LinkIcon, Eye, EyeOff } from "lucide-react";
import { Shell } from "../components/Shell";
import { request } from "../lib/api";

type IntakeChannel = "off" | "email" | "sms" | "manual_link";

interface CreateResult {
  ok: true;
  client: { id: string; legal_name: string; email: string };
  invitation: {
    public_url?: string;
    token: string;
    delivery_channel: string;
    delivered_to?: string | null;
    delivery_result?: any;
    delivery_error?: string;
    expires_at: string;
  } | null;
}

export default function NewClientPage() {
  const nav = useNavigate();

  const [firstName, setFirstName]   = useState("");
  const [middleName, setMiddleName] = useState("");
  const [lastName, setLastName]     = useState("");
  const [email, setEmail]           = useState("");
  const [phone, setPhone]           = useState("");

  const [dob, setDob]               = useState("");
  const [ssn, setSsn]               = useState("");
  const [addr1, setAddr1]           = useState("");
  const [addr2, setAddr2]           = useState("");
  const [city, setCity]             = useState("");
  const [stateCode, setStateCode]   = useState("");
  const [zip, setZip]               = useState("");

  const [monitoring, setMonitoring]       = useState<MonitoringProvider>("not_started");
  const [monUser, setMonUser]             = useState("");
  const [monPass, setMonPass]             = useState("");
  const [showMonPass, setShowMonPass]     = useState(false);
  const [scoreStart, setScoreStart]       = useState("");

  const [intakeChannel, setIntakeChannel] = useState<IntakeChannel>("off");

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<CreateResult | null>(null);
  const [copied, setCopied] = useState(false);

  const canSubmit =
    !!(firstName.trim() && lastName.trim() && email.trim()) &&
    (intakeChannel !== "sms" || phone.trim().length > 0);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    setBusy(true); setError(null);

    const send_intake_link =
      intakeChannel === "off" ? undefined :
      intakeChannel === "manual_link" ? { channel: "manual_link" } :
      intakeChannel === "email" ? { channel: "email", recipient: email.trim() } :
      { channel: "sms", recipient: phone.trim() };

    try {
      const r = await request<CreateResult>("/api/clients", {
        method: "POST",
        body: {
          first_name:  firstName.trim() || undefined,
          middle_name: middleName.trim() || undefined,
          last_name:   lastName.trim() || undefined,
          email:       email.trim(),
          phone:       phone.trim() || undefined,
          dob:         dob || undefined,
          ssn_full:    ssn.replace(/[^0-9]/g, '') || undefined,
          address_line1: addr1.trim() || undefined,
          address_line2: addr2.trim() || undefined,
          address_city:  city.trim() || undefined,
          address_state: stateCode.trim() || undefined,
          address_zip:   zip.trim() || undefined,
          monitoring,
          monitoring_username: monUser.trim() || undefined,
          monitoring_password: monPass || undefined,
          score_start: scoreStart ? Number(scoreStart) : undefined,
          send_intake_link,
        },
      });
      setResult(r);
    } catch (e: any) {
      setError(e?.message || "Could not create client");
    } finally {
      setBusy(false);
    }
  }

  function copyLink() {
    const url = result?.invitation?.public_url;
    if (!url) return;
    navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  }

  if (result) {
    const inv = result.invitation;
    return (
      <Shell title="Client created" subtitle="Onboarding" actions={<span />}>
        <div className="panel narrow-panel">
          <h2 style={{ marginTop: 0 }}>{result.client.legal_name}</h2>
          <p className="muted">{result.client.email}</p>

          {inv?.public_url && (
            <>
              <p style={{ marginTop: 18 }}>
                {inv.delivery_channel === "email" && !inv.delivery_error && "Intake link emailed."}
                {inv.delivery_channel === "sms"   && !inv.delivery_error && "Intake link texted."}
                {inv.delivery_channel === "manual_link" && "Copy the link below and send it however you like."}
                {inv.delivery_error && (
                  <span className="error-text">Send failed: {inv.delivery_error}. You can still copy the link.</span>
                )}
              </p>
              <div className="intake-url-row" style={{ marginTop: 8 }}>
                <code style={{ wordBreak: "break-all" }}>{inv.public_url}</code>
                <button className="icon-button" onClick={copyLink} title="Copy link" aria-label="Copy intake link">
                  <Copy size={14} aria-hidden="true" />
                </button>
              </div>
              {copied && <p className="muted small">Copied.</p>}
              <p className="muted small" style={{ marginTop: 8 }}>
                Link is single-use and expires {new Date(inv.expires_at).toLocaleDateString()}.
              </p>
            </>
          )}

          {!inv && (
            <p className="muted">No intake link generated. You can send one from the client page.</p>
          )}

          <div className="form-actions" style={{ marginTop: 24 }}>
            <button className="ghost-button" onClick={() => nav("/clients")}>Back to clients</button>
            <button className="primary-button" onClick={() => nav(`/clients/${result.client.id}`)}>
              Open client
            </button>
          </div>
        </div>
      </Shell>
    );
  }

  return (
    <Shell title="New client" subtitle="Onboarding" actions={<span />}>
      <div className="panel">
        <form className="stacked-form" onSubmit={submit}>

          <h3 style={{ marginTop: 0 }}>Name</h3>
          <div className="form-row-3">
            <label className="form-field">
              <span>First name *</span>
              <input value={firstName} onChange={(e) => setFirstName(e.target.value)} required />
            </label>
            <label className="form-field">
              <span>Middle name</span>
              <input value={middleName} onChange={(e) => setMiddleName(e.target.value)} />
            </label>
            <label className="form-field">
              <span>Last name *</span>
              <input value={lastName} onChange={(e) => setLastName(e.target.value)} required />
            </label>
          </div>

          <h3>Contact</h3>
          <div className="form-row-2">
            <label className="form-field">
              <span>Email *</span>
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
            </label>
            <label className="form-field">
              <span>Phone</span>
              <input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="(404) 555-0100" />
            </label>
          </div>

          <h3>Identity</h3>
          <div className="form-row-2">
            <label className="form-field">
              <span>Date of birth</span>
              <input type="date" value={dob} onChange={(e) => setDob(e.target.value)} />
            </label>
            <label className="form-field">
              <span>SSN</span>
              <input
                value={ssn}
                onChange={(e) => setSsn(e.target.value)}
                placeholder="123-45-6789"
                inputMode="numeric"
                autoComplete="off"
              />
            </label>
          </div>

          <h3>Address</h3>
          <label className="form-field">
            <span>Street</span>
            <input value={addr1} onChange={(e) => setAddr1(e.target.value)} />
          </label>
          <label className="form-field">
            <span>Apt / Suite</span>
            <input value={addr2} onChange={(e) => setAddr2(e.target.value)} />
          </label>
          <div className="form-row-3">
            <label className="form-field">
              <span>City</span>
              <input value={city} onChange={(e) => setCity(e.target.value)} />
            </label>
            <label className="form-field">
              <span>State</span>
              <input value={stateCode} onChange={(e) => setStateCode(e.target.value)} maxLength={2} placeholder="GA" />
            </label>
            <label className="form-field">
              <span>ZIP</span>
              <input value={zip} onChange={(e) => setZip(e.target.value)} inputMode="numeric" maxLength={10} />
            </label>
          </div>

          <h3>Credit monitoring (optional)</h3>
          <div className="form-row-2">
            <label className="form-field">
              <span>Provider</span>
              <select value={monitoring} onChange={(e) => setMonitoring(e.target.value as any)}>
                <option value="not_started">Not started</option>
                {MONITORING_PROVIDERS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
              </select>
            </label>
            <label className="form-field">
              <span>Starting score</span>
              <input
                value={scoreStart}
                onChange={(e) => setScoreStart(e.target.value)}
                inputMode="numeric"
                placeholder="e.g. 540"
              />
            </label>
          </div>
          {monitoring !== "not_started" && (
            <div className="form-row-2">
              <label className="form-field">
                <span>Username</span>
                <input value={monUser} onChange={(e) => setMonUser(e.target.value)} autoComplete="off" />
              </label>
              <label className="form-field">
                <span>Password</span>
                <div className="input-with-affix">
                  <input
                    type={showMonPass ? "text" : "password"}
                    value={monPass}
                    onChange={(e) => setMonPass(e.target.value)}
                    autoComplete="off"
                  />
                  <button
                    type="button"
                    onClick={() => setShowMonPass(s => !s)}
                    title={showMonPass ? "Hide password" : "Show password"}
                    aria-label={showMonPass ? "Hide password" : "Show password"}
                    aria-pressed={showMonPass}
                    className="input-affix-btn"
                  >
                    {showMonPass ? <EyeOff size={16} aria-hidden="true" /> : <Eye size={16} aria-hidden="true" />}
                  </button>
                </div>
              </label>
            </div>
          )}

          <h3>Intake link</h3>
          <p className="muted small">
            Optionally send the client a secure link to fill in their own info. They get a focused page;
            anything they enter overwrites blanks above and shows up in the CRM.
          </p>
          <div className="channel-picker" style={{ marginBottom: 8 }}>
            <button
              type="button"
              className={intakeChannel === "off" ? "active" : ""}
              aria-pressed={intakeChannel === "off"}
              onClick={() => setIntakeChannel("off")}
            >
              Don't send
            </button>
            <button
              type="button"
              className={intakeChannel === "email" ? "active" : ""}
              aria-pressed={intakeChannel === "email"}
              onClick={() => setIntakeChannel("email")}
            >
              <Mail size={16} aria-hidden="true" /> Email it
            </button>
            <button
              type="button"
              className={intakeChannel === "sms" ? "active" : ""}
              aria-pressed={intakeChannel === "sms"}
              onClick={() => setIntakeChannel("sms")}
            >
              <MessageCircle size={16} aria-hidden="true" /> Text it
            </button>
            <button
              type="button"
              className={intakeChannel === "manual_link" ? "active" : ""}
              aria-pressed={intakeChannel === "manual_link"}
              onClick={() => setIntakeChannel("manual_link")}
            >
              <LinkIcon size={16} aria-hidden="true" /> Just give me the link
            </button>
          </div>
          {intakeChannel === "email" && (
            <p className="muted small">Will go to <strong>{email || "(enter email above)"}</strong></p>
          )}
          {intakeChannel === "sms" && (
            <p className="muted small">Will text <strong>{phone || "(enter phone above)"}</strong></p>
          )}
          {intakeChannel === "manual_link" && (
            <p className="muted small">After creating, you'll get a link to copy and send however you want.</p>
          )}

          {error && <p className="error-text">{error}</p>}

          <div className="form-actions" style={{ marginTop: 20 }}>
            <button type="button" className="ghost-button" onClick={() => nav("/clients")}>Cancel</button>
            <button className="primary-button" disabled={busy || !canSubmit}>
              {busy
                ? "Creating…"
                : intakeChannel === "off"
                ? "Create client"
                : "Create + send intake"}
            </button>
          </div>
        </form>
      </div>
    </Shell>
  );
}
